// ============================================================================
// Response API Service
// ============================================================================

/**
 * Response API service for survey response submission and management.
 * Provides typed methods for interacting with the backend response endpoints.
 */

import { api } from '@/services/api';

// ============================================================================
// Types
// ============================================================================

/**
 * Status of a survey response.
 *
 * - in_progress: Response is being filled out, can be modified
 * - submitted: Response is finalized and immutable
 */
export type ResponseStatus = 'in_progress' | 'submitted';

/**
 * Answer data for a single question.
 *
 * @remarks
 * - question_id must reference a valid question in the survey
 * - answer_value type depends on question type
 */
export interface ResponseAnswerDto {
  /**
   * UUID of the Question being answered
   */
  question_id: string;

  /**
   * The participant's response value
   * Type varies by question type:
   * - TEXT: string
   * - SINGLE_SELECT: string (option ID)
   * - MULTI_SELECT: string[] (array of option IDs)
   * - NUMBER: number
   * - SLIDER: number
   * - DATE: string (ISO 8601 date string)
   */
  answer_value: unknown;

  /**
   * Optional note paired with the answer when free_text is enabled
   */
  answer_note?: string;
}

/**
 * DTO for creating a new response (in_progress status).
 *
 * @remarks
 * - Used to start a new survey response
 * - Creates a response in 'in_progress' status
 */
export interface CreateResponseDto {
  /**
   * UUID of the SurveyVersion this response is for
   */
  survey_version_id: string;

  /**
   * Optional identifier for the participant
   */
  participant_identifier?: string;

  /**
   * Initial status of the response
   * @default 'in_progress'
   */
  status?: ResponseStatus;
}

/**
 * DTO for submitting a complete response with all answers.
 *
 * @remarks
 * - Creates a response in 'submitted' status with all answers atomically
 * - Used for single-step submission from the patient survey form
 * - client_timestamp used for device time tamper detection (IRB/MFDS compliance)
 * - device_id, device_type, site_id used for Site vs Patient Device duplicate prevention
 * - idempotency_key used for preventing duplicate submissions on retry (offline sync)
 */
export interface SubmitResponseDto {
  /**
   * UUID of the SurveyVersion this response is for
   */
  survey_version_id: string;

  /**
   * Optional session ID (survey assignment ID) for tracking which assignment this response fulfills
   */
  session_id?: string;

  /**
   * Optional identifier for the participant
   */
  participant_identifier?: string;

  /**
   * Array of answers to individual questions
   */
  answers: ResponseAnswerDto[];

  /**
   * Client-side timestamp when submission was initiated (ISO 8601 string)
   * Used for device time tamper detection (regulatory compliance)
   */
  client_timestamp?: string;

  /**
   * Device identifier for duplicate prevention
   * Should be consistent across sessions on same device
   * Example: browser fingerprint, hardware ID, or UUID
   */
  device_id?: string;

  /**
   * Device type for duplicate prevention
   * CRITICAL: Used to prevent double submission between Site iPad and Patient Device
   * - site_tablet: Survey completed at clinical site on Site iPad
   * - patient_device: Survey completed by patient on their personal device
   */
  device_type?: 'site_tablet' | 'patient_device';

  /**
   * Site identifier for multi-site deployments
   * Links response to specific clinical site
   */
  site_id?: string;

  /**
   * Idempotency key for preventing duplicate submissions on retry (UUID v4)
   * Generated by client for offline submissions
   * Ensures that network retries don't create duplicate responses
   * IRB/MFDS compliance: guarantees exactly-once semantics
   */
  idempotency_key?: string;

  /**
   * Reason for modifying a previously submitted response (patient resubmit after correction query).
   * When provided, backend logs RESPONSE_MODIFIED audit event with this reason.
   */
  modification_reason?: string;

  /**
   * Flag indicating response was completed offline
   * Used for tracking offline → online sync flow
   * IRB/MFDS compliance: audit trail for offline submissions
   */
  was_offline?: boolean;

  /**
   * Client timestamp when response was completed offline (ISO 8601 string)
   * Used to calculate sync delay variance
   * IRB/MFDS compliance: timestamp variance tracking
   */
  offline_completed_at?: string;
}

/**
 * Answer change record for tracking modifications
 */
export interface AnswerChangeDto {
  /** Question ID */
  question_id: string;
  /** Previous answer value */
  old_value: string | string[];
  /** New answer value */
  new_value: string | string[];
}

/**
 * DTO for modifying an existing response (PUT /responses/:id/modify)
 *
 * @remarks
 * - Used when editing a SUBMITTED survey
 * - Requires modification_reason (minimum 10 characters)
 * - Tracks answer changes for audit trail
 * - Increments update_count automatically
 */
export interface ModifyResponseDto {
  /**
   * Reason for modifying the response
   * Required for audit trail and regulatory compliance
   * Minimum 10 characters
   */
  modification_reason: string;

  /**
   * English reason for modification when bilingual audit text is needed.
   */
  modification_reason_en?: string;

  /**
   * Complete set of new answers
   * Replaces all existing answers
   *
   * @remarks
   * Backend automatically calculates answer_changes by comparing
   * existing answers with the new answers. Frontend only needs to
   * send modification_reason and answers.
   */
  answers: ResponseAnswerDto[];

  /**
   * Device type of the resubmission context.
   * Used so backend can preserve CRC-vs-PATIENT submitter semantics on query resubmits.
   */
  device_type?: 'site_tablet' | 'patient_device';
}

/**
 * Response entity returned from the API.
 *
 * @remarks
 * - Contains response metadata and optional answers
 * - Timestamps are ISO 8601 strings
 * - Includes device information for duplicate prevention tracking
 */
export interface ResponseDto {
  /**
   * Unique identifier (UUID)
   */
  id: string;

  /**
   * UUID of the SurveyVersion this response is for
   */
  survey_version_id: string;

  /**
   * Optional identifier for the participant
   */
  participant_identifier?: string;

  /**
   * Current status of the response
   */
  status: ResponseStatus;

  /**
   * Date/time when this response was submitted (ISO 8601 string)
   * Null if not yet submitted
   */
  submitted_at?: string;

  /**
   * Timestamp when the response was created (ISO 8601 string)
   */
  created_at: string;

  /**
   * Timestamp when the response was last updated (ISO 8601 string)
   */
  updated_at?: string;

  /**
   * Device identifier that submitted this response
   */
  device_id?: string;

  /**
   * Device type that submitted this response
   */
  device_type?: 'site_tablet' | 'patient_device';

  /**
   * Site identifier if submitted from a clinical site
   */
  site_id?: string;

  /**
   * Collection of answers (when loaded with relations)
   */
  answers?: ResponseAnswerEntityDto[];
}

/**
 * Response answer entity returned from the API.
 */
export interface ResponseAnswerEntityDto {
  /**
   * Unique identifier (UUID)
   */
  id: string;

  /**
   * UUID of the parent Response
   */
  response_id: string;

  /**
   * UUID of the Question being answered
   */
  question_id: string;

  /**
   * The answer value (JSON)
   */
  answer_value: unknown;

  /**
   * Optional free text note paired with the answer
   */
  answer_note?: string;

  /**
   * Timestamp when created (ISO 8601 string)
   */
  created_at: string;

  /**
   * Timestamp when updated (ISO 8601 string)
   */
  updated_at?: string;
}

// ============================================================================
// Response Creation
// ============================================================================

/**
 * Creates a new response in 'in_progress' status.
 *
 * @param dto - Response creation data
 * @returns The created response
 *
 * @throws {ApiError} When creation fails (e.g., invalid survey version)
 *
 * @example
 * ```typescript
 * const response = await createResponse({
 *   survey_version_id: 'version-uuid-123',
 *   participant_identifier: 'patient-abc',
 * });
 * ```
 */
export async function createResponse(dto: CreateResponseDto): Promise<ResponseDto> {
  return api.post<ResponseDto, CreateResponseDto>('/responses', dto);
}

// ============================================================================
// Response Submission
// ============================================================================

/**
 * Submits a complete response with all answers atomically.
 *
 * @param dto - Complete submission data including answers
 * @returns The created and submitted response
 *
 * @throws {ApiError} When submission fails
 *
 * @remarks
 * - Creates a new response in 'submitted' status
 * - All answers are saved atomically
 * - Sets submitted_at timestamp
 *
 * @example
 * ```typescript
 * const response = await submitResponse({
 *   survey_version_id: 'version-uuid-123',
 *   participant_identifier: 'patient-abc',
 *   answers: [
 *     { question_id: 'q1', answer_value: 'Yes' },
 *     { question_id: 'q2', answer_value: 5 },
 *   ],
 * });
 * ```
 */
export async function submitResponse(dto: SubmitResponseDto): Promise<ResponseDto> {
  return api.post<ResponseDto, SubmitResponseDto>('/responses/submit', dto);
}

// ============================================================================
// Response Retrieval
// ============================================================================

/**
 * Fetches a response by ID.
 *
 * @param id - UUID of the response
 * @returns The response data
 *
 * @throws {ApiError} When the response is not found
 *
 * @example
 * ```typescript
 * const response = await getResponse('response-uuid-123');
 * ```
 */
export async function getResponse(id: string): Promise<ResponseDto> {
  return api.get<ResponseDto>(`/responses/${encodeURIComponent(id)}`);
}

/**
 * Fetches the most recent response for a session.
 *
 * @param sessionId - UUID of the session (survey assignment)
 * @returns The response data with answers
 *
 * @throws {ApiError} When the response is not found
 *
 * @remarks
 * - Used for loading previous answers when restarting a SUBMITTED survey
 * - Returns the response with all answers included
 *
 * @example
 * ```typescript
 * const response = await getResponseBySessionId('session-uuid-123');
 * console.log(response.answers); // Array of ResponseAnswerEntityDto
 * ```
 */
export async function getResponseBySessionId(sessionId: string): Promise<ResponseDto> {
  return api.get<ResponseDto>(`/responses/session/${encodeURIComponent(sessionId)}`);
}

/**
 * Fetches the most recent response for the authenticated participant's session.
 *
 * Used by patient-web resubmit/query flows because /responses/session/:id is
 * protected by staff RBAC.
 */
export async function getPatientResponseBySessionId(sessionId: string): Promise<ResponseDto> {
  return api.get<ResponseDto>(`/responses/patient/session/${encodeURIComponent(sessionId)}`);
}

/**
 * Fetches all responses with optional filtering.
 *
 * @param options - Optional filter parameters
 * @returns Array of matching responses
 *
 * @example
 * ```typescript
 * // Get all submitted responses for a survey version
 * const responses = await getResponses({
 *   status: 'submitted',
 *   survey_version_id: 'version-uuid-123',
 * });
 * ```
 */
export async function getResponses(options?: {
  status?: ResponseStatus;
  survey_version_id?: string;
}): Promise<ResponseDto[]> {
  const params = new URLSearchParams();
  if (options?.status) {
    params.append('status', options.status);
  }
  if (options?.survey_version_id) {
    params.append('survey_version_id', options.survey_version_id);
  }

  const queryString = params.toString();
  const endpoint = queryString ? `/responses?${queryString}` : '/responses';

  return api.get<ResponseDto[]>(endpoint);
}

// ============================================================================
// Response Updates
// ============================================================================

/**
 * Updates a response.
 *
 * @param id - UUID of the response to update
 * @param dto - Partial response data to update
 * @returns The updated response
 *
 * @throws {ApiError} When the response is not found or update fails
 *
 * @remarks
 * - Only non-submitted responses can be updated
 * - Use submitResponseById to transition to submitted status
 *
 * @example
 * ```typescript
 * const updated = await updateResponse('response-uuid-123', {
 *   participant_identifier: 'updated-patient-id',
 * });
 * ```
 */
export async function updateResponse(
  id: string,
  dto: Partial<CreateResponseDto>
): Promise<ResponseDto> {
  return api.put<ResponseDto, Partial<CreateResponseDto>>(
    `/responses/${encodeURIComponent(id)}`,
    dto
  );
}

/**
 * Submits an existing response (changes status to 'submitted').
 *
 * @param id - UUID of the response to submit
 * @returns The submitted response
 *
 * @throws {ApiError} When the response is not found or already submitted
 *
 * @remarks
 * - Transitions response from 'in_progress' to 'submitted'
 * - Sets submitted_at timestamp
 * - Response becomes immutable after submission
 *
 * @example
 * ```typescript
 * const submitted = await submitResponseById('response-uuid-123');
 * ```
 */
export async function submitResponseById(id: string): Promise<ResponseDto> {
  return api.post<ResponseDto, undefined>(
    `/responses/${encodeURIComponent(id)}/submit`,
    undefined
  );
}

// ============================================================================
// Response Deletion
// ============================================================================

/**
 * Deletes a response.
 *
 * @param id - UUID of the response to delete
 *
 * @throws {ApiError} When the response is not found
 *
 * @remarks
 * - This action cannot be undone
 * - All associated answers are also deleted
 *
 * @example
 * ```typescript
 * await deleteResponse('response-uuid-123');
 * ```
 */
export async function deleteResponse(id: string): Promise<void> {
  return api.delete<void>(`/responses/${encodeURIComponent(id)}`);
}

/**
 * Modifies an existing response (for editing SUBMITTED surveys).
 *
 * @param id - UUID of the response to modify
 * @param dto - Modification data including reason and new answers
 *
 * @returns Updated response with incremented update_count
 *
 * @throws {ApiError} When the response is not found or validation fails
 *
 * @remarks
 * - Only works for responses in SUBMITTED state
 * - Requires modification_reason (minimum 10 characters)
 * - Creates audit log entry for compliance
 * - Increments update_count automatically
 * - Tracks answer changes (old_value → new_value)
 *
 * @example
 * ```typescript
 * const modifiedResponse = await modifyResponse('response-uuid-123', {
 *   modification_reason: 'Correcting initial answer for question 2',
 *   answer_changes: [
 *     {
 *       question_id: 'q2',
 *       old_value: '3',
 *       new_value: '5'
 *     }
 *   ],
 *   answers: [
 *     { question_id: 'q1', answer_value: 'Yes' },
 *     { question_id: 'q2', answer_value: '5' }
 *   ]
 * });
 * console.log(modifiedResponse.update_count); // incremented by 1
 * ```
 */
export async function modifyResponse(
  id: string,
  dto: ModifyResponseDto
): Promise<ResponseDto> {
  return api.put<ResponseDto>(`/responses/${encodeURIComponent(id)}/modify`, dto);
}

/**
 * Modifies an existing response using patient JWT (for correction query resubmit).
 *
 * @param id - UUID of the response to modify
 * @param dto - Modification data including reason and new answers
 * @returns Updated response
 *
 * @remarks
 * - Same as modifyResponse but authenticated with patient JWT token
 * - Used when patient corrects answers after CRC issues a correction query
 */
export async function modifyResponseByPatient(
  id: string,
  dto: ModifyResponseDto
): Promise<ResponseDto> {
  return api.put<ResponseDto>(`/responses/${encodeURIComponent(id)}/modify-by-patient`, dto);
}

// ============================================================================
// Exports
// ============================================================================

/**
 * Response API service object with all response-related methods.
 * Alternative to importing individual functions.
 *
 * @example
 * ```typescript
 * import { responseApi } from '@/services/responseApi';
 *
 * const response = await responseApi.submitResponse({
 *   survey_version_id: 'version-uuid-123',
 *   answers: [...],
 * });
 * ```
 */
export const responseApi = {
  createResponse,
  submitResponse,
  getResponse,
  getResponseBySessionId,
  getPatientResponseBySessionId,
  getResponses,
  updateResponse,
  submitResponseById,
  deleteResponse,
  modifyResponse,
  modifyResponseByPatient,
} as const;

export default responseApi;
